import React, { useState } from 'react';
import { DateFormat } from '../../utils/chartTypes';
import {
  CommaFormattedString,
  Sign,
  SignAddedCommaFormattedString,
} from '../../utils/formatter';
import Icon from 'components/commons/icon/Icon';
import {
  ticksToPlotLines,
  ticksToTickInterval,
} from 'components/features/new-report/utils/util';
import { getSignColor } from 'components/features/new-report/yearly-report/YearlyReport';
import MessageModal from 'components/features/modal/MessageModal';
import { formatDateForString } from 'utils/formatter';
import { GetMonthlyReportResult } from 'config/api';

export type ComparisonBetweenIndexAndAssetProps = {
  periodUnit: string;
  comparisonSign: Sign;
  comparisonPercentage: SignAddedCommaFormattedString;
  valuation: CommaFormattedString;
  rowData: (Omit<RowProps, 'isChecked' | 'toggleCheck'> & { id: RowId })[];
  wholeData: WholeData;
  latestMyAssetRate: number;
  tickInterval: number;
  dateFormat: DateFormat;
  Chart: React.FC<ChartProps>;
};

type DescriptionProps = {
  periodUnit: string;
};

type ManagedAssetsMaterialProps = {
  comparisonSign: Sign;
  comparisonPercentage: SignAddedCommaFormattedString;
  valuation: CommaFormattedString;
};

type Tagged<S extends string, T> = { [key in S]: never } & T;
type TaggedNumber<S extends string> = Tagged<S, number>;
export type Mine = TaggedNumber<'Mine'>;
export const createMine = (v: number): Mine => v as Mine;
export type NK225 = TaggedNumber<'NK225'>;
export const createNK225 = (v: number): NK225 => v as NK225;
export type TOPIX = TaggedNumber<'TOPIX'>;
export const createTOPIX = (v: number): TOPIX => v as TOPIX;
export type DJI = TaggedNumber<'DJI'>;
export const createDJI = (v: number): DJI => v as DJI;
export type SSI = TaggedNumber<'SSI'>;
export const createSSI = (v: number): SSI => v as SSI;
export type USDJPY = TaggedNumber<'USDJPY'>;
export const createUSDJPY = (v: number): USDJPY => v as USDJPY;
export type BTCJPY = TaggedNumber<'BTCJPY'>;
export const createBTCJPY = (v: number): BTCJPY => v as BTCJPY;
export type WholeData = [
  Date,
  boolean,
  Mine | null,
  NK225 | null,
  TOPIX | null,
  DJI | null,
  SSI | null,
  USDJPY | null,
  BTCJPY | null
][];
const indexKeyToIndex = (key: IndexKey): 2 | 3 | 4 | 5 | 6 | 8 => {
  switch (key) {
    case 'NK225':
      return 2;
    case 'TOPIX':
      return 3;
    case 'DJI':
      return 4;
    case 'SSI':
      return 5;
    case 'USDJPY':
      return 6;
    case 'BTCJPY':
      return 8;
  }
};

type DataHeader = [{ type: 'date'; label: 'Date' }, ...string[]];
type DataContent = [Date, boolean, ...(number | null)[]][];
export const filteredDataMineIndex = 1;
export type ChartProps = {
  data: DataContent;
  header: DataHeader;
  colors: string[];
  selectedHeaders: string[];
  changeVisibility: (header: string, visible: boolean) => void;
  xAxisInfo: Highcharts.XAxisOptions;
};
type MultiLineChartProps = {
  Chart: React.FC<ChartProps>;
  wholeData: WholeData;
  ids: IndexKey[];
  tickInterval: number;
  dateFormat: DateFormat;
  changeVisibility: (id: IndexKey, visible: boolean) => void;
};

type SelectTableProps = {
  rowData: (Omit<RowProps, 'isChecked' | 'toggleCheck'> & { id: RowId })[];
  checkedIds: RowId[];
  setCheckedIds: React.Dispatch<React.SetStateAction<RowId[]>>;
};

export type IndexKey = Exclude<
  keyof Exclude<GetMonthlyReportResult, null>['Indexes'][number],
  'Day' | 'Mine'
>;
export const INDEX_TYPE = {
  NK225: 'type1',
  TOPIX: 'type2',
  DJI: 'type3',
  SSI: 'type4',
  USDJPY: 'type5',
  BTCJPY: 'type7',
} as const;
type IndexType = typeof INDEX_TYPE[keyof typeof INDEX_TYPE];
type RowId = IndexKey;
type RowProps = {
  isChecked: boolean;
  toggleCheck: () => void;
  indexName: string;
  indexType: IndexType;
  indexSign: Sign;
  indexChangeRatio: SignAddedCommaFormattedString;
  indexValue: CommaFormattedString;
  indexValueDatetime: string;
};
export const myAssetColor = '#ef4444';

const Description: React.FC<DescriptionProps> = ({ periodUnit }) => {
  return (
    <>
      <div className="flex items-center mb-3 text-base font-bold text-bluegray-700">
        <img
          src="/static/images/report/icon-chart.svg"
          alt=""
          className="mr-2"
        />
        主要指標と自分の資産を比較
      </div>
      <div className="text-xs text-black">
        あなたの資産と主要指標の1{periodUnit}
        の変動率を見比べることができます。
      </div>
    </>
  );
};

const MultiLineChart: React.FC<MultiLineChartProps> = ({
  Chart,
  wholeData: data,
  ids: selectedIds,
  tickInterval,
  dateFormat,
  changeVisibility: changeVisibility_,
}) => {
  const allIds: IndexKey[] = [
    'BTCJPY',
    'DJI',
    'NK225',
    'SSI',
    'TOPIX',
    'USDJPY',
  ];
  const allSortedIds = allIds.sort(
    (a, b) => indexKeyToIndex(a) - indexKeyToIndex(b)
  );
  const yourAsset = 'あなたの運用資産';
  const header: DataHeader = [
    { type: 'date', label: 'Date' },
    yourAsset,
    ...allSortedIds.map(id => {
      return apiKeyToName(id);
    }),
  ];
  const selectedHeaders = [
    yourAsset,
    ...selectedIds.map(id => apiKeyToName(id)),
  ];
  const colors = [myAssetColor, ...allSortedIds.map(id => indexKeyToColor(id))];
  const firstDay = data[0] ? data[0][0] : new Date();
  const note =
    dateFormat === 'yyyy/MM'
      ? `${firstDay.getFullYear()}/${firstDay.getMonth() +
          1}/${firstDay.getDate()}の終値=0%とする`
      : `${firstDay.getMonth() + 1}/${firstDay.getDate()}の終値=0%とする`;
  const changeVisibility = (header: string, visibility: boolean) => {
    const id = allIds.find(i => {
      return apiKeyToName(i) === header;
    });
    if (id) changeVisibility_(id, visibility);
  };

  let xAxisInfo_ = [] as Highcharts.XAxisOptions;
  if (dateFormat === 'yyyy/MM') {
    // MEMO: highChartsのticksに表示する値のindexを抽出する
    const tickLength = data.length > 60 ? 3 : 2;
    const tickIntervalLength = Math.ceil(data.length / tickLength);
    const arr = new Array(tickLength).fill(0);

    const ticks = arr.map((_, i) => {
      return i * tickIntervalLength;
    });

    xAxisInfo_ = {
      type: 'datetime',
      tickPositions: ticks,
      categories: data.map(r =>
        ((date): string => {
          return `${date.getFullYear()}/${date.getMonth() +
            1}/${date.getDate()}`;
        })(r[0])
      ),
      labels: {
        formatter: function(): string {
          return `${formatDateForString(this.value.toString(), 'yyyy/M')}`;
        },
      },
      plotLines: ticks.map(tick => {
        return {
          color: '#dddddd',
          value: tick,
          width: 1,
        };
      }),
    } as Highcharts.XAxisOptions;
  } else {
    const ticks = data
      .filter((_, i) => i % tickInterval === 0)
      .map(datum => {
        return datum[0];
      });
    xAxisInfo_ = {
      tickInterval: ticksToTickInterval(ticks, 1),
      categories: data.map(r =>
        ((date, dateFormat): string => {
          const dateString = `${date.getMonth() + 1}/${date.getDate()}`;
          const day = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()];
          if (dateFormat === 'M/d\nE')
            return `<span class="tooltip-date-label"><span class="date">${dateString}</span><br/><span class="day">${day}</span></span>`;
          return dateString;
        })(r[0], dateFormat)
      ),
      plotLines: ticksToPlotLines(ticks),
    } as Highcharts.XAxisOptions;
  }

  const xAxisInfo = {
    ...xAxisInfo_,
    crosshair: {
      snap: true,
      width: 1,
      color: '#777',
    },
  } as Highcharts.XAxisOptions;

  return (
    <>
      <div className="w-full">
        <span className="text-1.5xs text-bluegray-500">{note}</span>
        <Chart
          data={data}
          header={header}
          colors={colors}
          selectedHeaders={selectedHeaders}
          changeVisibility={changeVisibility}
          xAxisInfo={xAxisInfo}
        />
      </div>
    </>
  );
};

const ManagedAssetsMaterial: React.FC<ManagedAssetsMaterialProps> = ({
  comparisonSign,
  comparisonPercentage,
  valuation,
}) => {
  return (
    <>
      <table className="w-full mb-4">
        <thead>
          <tr className="text-xs text-bluegray-500 bg-transparent">
            <th></th>
            <th></th>
            <th>変動率</th>
            <th>評価額</th>
          </tr>
        </thead>
        <tbody>
          <tr className="bg-bluegray-50">
            <td className="py-2 px-1" style={{ width: '30px' }}>
              <div
                className="my-0 mx-1 bg-red-500"
                style={{ width: '14px', height: '6px' }}></div>
            </td>
            <td className="py-2 px-1 text-xs font-bold">あなたの資産</td>
            <td
              className={`py-2 px-1 text-sm font-bold text-right ${getSignColor(
                comparisonSign
              )}`}>
              {comparisonPercentage}%
            </td>
            <td className="py-2 pl-1 pr-4 text-xs text-right text-bluegray-500 ">
              {valuation}円
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

const SelectTable: React.FC<SelectTableProps> = ({
  rowData,
  checkedIds,
  setCheckedIds,
}) => {
  const Header: React.FC<Record<string, unknown>> = () => (
    <thead>
      <tr>
        <th className="py-1 px-4 text-xs font-normal text-bluegray-500 border-b border-bluegray-200">
          <label className="btn-checkbox flex items-center cursor-pointer">
            <input
              type="checkbox"
              onChange={toggleCheckAll}
              checked={isAllChecked}
            />
            <span
              className={`btn-check-label ${isAllChecked ? 'font-bold' : ''}`}>
              一括選択
            </span>
          </label>
        </th>
        <th className="py-1 px-4 text-xs font-normal text-center text-bluegray-500 border-b border-bluegray-200">
          変動率
        </th>
        <th className="py-1 px-4 text-xs font-normal text-center text-bluegray-400 border-b border-bluegray-200">
          値
        </th>
      </tr>
    </thead>
  );

  const Row: React.FC<RowProps> = ({
    isChecked,
    indexName,
    indexType,
    indexSign,
    indexChangeRatio,
    indexValue,
    indexValueDatetime,
    toggleCheck,
  }) => {
    const getLabelColor = (indexType: IndexType) => {
      switch (indexType) {
        case 'type1':
          return 'bg-indigo-500';
        case 'type2':
          return 'bg-sky-400';
        case 'type3':
          return 'bg-green-500';
        case 'type4':
          return 'bg-pink-400';
        case 'type5':
          return 'bg-orange-500';
        case 'type7':
          return 'bg-fuchsia-600';
      }
    };
    return (
      <>
        <tr>
          <td className="py-2 px-4 border-b border-bluegray-200">
            <label className="btn-checkbox flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={toggleCheck}
              />
              <span
                className={`btn-check-label ${isChecked ? 'font-bold' : ''}`}>
                <span
                  className={`w-3.5 h-1.5 mr-1 ${getLabelColor(indexType)} ${
                    isChecked ? '' : 'opacity-10'
                  }`}></span>
                {indexName}
              </span>
            </label>
          </td>
          <td className="py-2 px-4 text-center border-b border-bluegray-200">
            <div
              className={`text-sm font-bold ${
                isChecked ? getSignColor(indexSign) : 'text-bluegray-400'
              }`}>
              {indexChangeRatio}%
            </div>
          </td>
          <td
            className={`py-2 px-4 text-center text-bluegray-400 border-b border-bluegray-200 ${
              isChecked ? 'text-bluegray-500' : ''
            }`}>
            <div className="text-xs">{indexValue}</div>
            <div className="text-1.5xs">({indexValueDatetime})</div>
          </td>
        </tr>
      </>
    );
  };

  const toggleCheckAll = (): void => {
    setCheckedIds(prev => {
      if (prev.length === rowData.length) return [];
      return rowData.map(d => d.id);
    });
  };
  const isAllChecked =
    rowData.length !== 0 && checkedIds.length === rowData.length;

  const toggleCheck = (id: RowId) => (): void => {
    setCheckedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  return (
    <>
      <table className="w-full">
        <Header />
        <tbody>
          {rowData
            .sort((a, b) => indexKeyToIndex(a.id) - indexKeyToIndex(b.id))
            .map((p, i) => {
              return (
                <Row
                  key={`${p.id}${i}`}
                  {...p}
                  isChecked={checkedIds.includes(p.id)}
                  toggleCheck={toggleCheck(p.id)}
                />
              );
            })}
        </tbody>
      </table>
    </>
  );
};

const Hint: React.FC<Record<string, unknown>> = () => (
  <div className="p-4 overflow-hidden">
    <div className="py-2 px-4 bg-bluegray-50 rounded-7">
      <div className="mb-1 text-teal-500 text-xs leading-6">活用のヒント</div>
      <div className="flex">
        <div
          className="mr-2 rounded-full"
          style={{ height: '50px', width: '50px', minWidth: '50px' }}>
          <img
            src="/static/images/advisor/ai_img_2.svg"
            className="w-full rounded-full"
            alt=""
          />
        </div>
        <div className="text-sm text-bluegray-700">
          <div className="text-xs font-bold">
            あなたの資産と同じ動きをしているチャートは？
          </div>
          <div className="mt-1 text-sm">
            あなたの運用資産が影響を受けているのはどの指標ですか？同じカタチをしているチャートを見つけてみましょう。
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const ComparisonBetweenIndexAndAsset: React.FC<ComparisonBetweenIndexAndAssetProps> = props => {
  const {
    periodUnit,
    comparisonSign,
    comparisonPercentage,
    valuation,
    rowData,
    wholeData,
    tickInterval,
    dateFormat,
    Chart,
  } = props;
  const [checkedIds, setCheckedIds] = useState<RowId[]>(
    rowData.flatMap(row => {
      if (row.id !== 'NK225' && row.id !== 'DJI') return [];
      return [row.id];
    })
  );
  const [showModal, setShowModal] = useState(false);
  const onInfoClick = (): void => {
    setShowModal(true);
  };
  const onClose = (): void => {
    setShowModal(false);
  };
  const changeVisibility = (id: RowId, visibility: boolean) => {
    if (visibility) {
      setCheckedIds(prev => {
        return [...prev.filter(i => i !== id), id];
      });
    } else {
      setCheckedIds(prev => {
        return [...prev.filter(i => i !== id)];
      });
    }
  };

  return !isComparisonBetweenIndexAndAssetEmpty(props) ? (
    <>
      <div className="p-4 overflow-hidden">
        <Description periodUnit={periodUnit} />
        <ManagedAssetsMaterial
          comparisonSign={comparisonSign}
          comparisonPercentage={comparisonPercentage}
          valuation={valuation}
        />
        <div
          className="text-gray-500 text-sm text-right cursor-pointer"
          onClick={onInfoClick}>
          <span>
            <Icon className="pr-1" name="info" />
            資産データについて
          </span>
        </div>
        <MultiLineChart
          Chart={Chart}
          ids={checkedIds}
          wholeData={wholeData}
          tickInterval={tickInterval}
          dateFormat={dateFormat}
          changeVisibility={changeVisibility}
        />
      </div>
      <div className="">
        <SelectTable
          checkedIds={checkedIds}
          setCheckedIds={setCheckedIds}
          rowData={rowData}
        />
        <Hint />
      </div>
      {showModal && (
        <div onClick={showModal && onClose}>
          <MessageModal
            title={''}
            message={[
              '「みんかぶ口座連携」で連携した口座は土日祝日はデータ更新を行いません。',
              '前回取得成功したデータを暫定値として表示しています。',
            ]}
            onClose={onClose}
          />
        </div>
      )}
    </>
  ) : null;
};

export const isComparisonBetweenIndexAndAssetEmpty = ({
  wholeData,
}: ComparisonBetweenIndexAndAssetProps): boolean => {
  return wholeData.length === 0;
};

export const apiKeyToName = (key: IndexKey): string => {
  switch (key) {
    case 'BTCJPY':
      return 'ビットコイン/円';
    case 'DJI':
      return 'NYダウ';
    case 'NK225':
      return '日経平均';
    case 'SSI':
      return '上海総合';
    case 'TOPIX':
      return 'TOPIX';
    case 'USDJPY':
      return '米ドル/円';
  }
};

export type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

const indexKeyToColor = (key: IndexKey): string => {
  switch (key) {
    case 'NK225':
      return '#6366f1';
    case 'TOPIX':
      return '#38bdf8';
    case 'DJI':
      return '#22c55e';
    case 'SSI':
      return '#f472b6';
    case 'USDJPY':
      return '#f97316';
    case 'BTCJPY':
      return '#c026d3';
  }
};
